import React, {
  FC,
  useRef,
  useState,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';

import { IoMdEye } from 'react-icons/io';
import { MdAddAPhoto, MdCheckCircle } from 'react-icons/md';

import {
  Box,
  Text,
  FormLabel,
  FormControl,
  Progress as ChakraProgress,
  useToast,
} from '@chakra-ui/react';

import { useField } from 'formik';

import { uploadImage } from 'api/site';
import { uploadGroupImage } from 'api/groups';
import { useMutation } from 'react-query';

import Preview from './Preview';
import ImageCropper from './ImageCropper';

import { FormikImageInputViewProps } from './FormikImageInput.props';

const Progress = forwardRef<any, any>((_, ref) => {
  const [progress, setProgress] = useState(0);

  useImperativeHandle(
    ref,
    () => ({
      setProgress: (p: number) => setProgress(p),
    }),
    [],
  );

  return (
    <Box
      display="grid"
      alignItems="center"
      gridTemplateColumns="4fr 1fr"
      gridGap="0px 5px">
      <ChakraProgress
        size="sm"
        zIndex={1}
        value={progress}
        borderRadius="20px"
        colorScheme="support"
        hasStripe
      />
      <Text fontSize="12px">{progress.toFixed(2)}%</Text>
    </Box>
  );
});

const FormikImageInputView: FC<FormikImageInputViewProps> = ({
  name,
  label,
  siteId = '',
  groupId,
  labelProps = {},
  isDisabled,
  aspectRatio,
  containerProps,
  isPartnerPortal,
}) => {
  const toast = useToast();

  const previewRef = useRef<any>(null);
  const progressRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [field, meta, helpers] = useField<string>(name);
  const [, , loadingHelpers] = useField('_isUploading');

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const { mutate, isSuccess, isLoading } = useMutation(
    (_file: File) =>
      groupId
        ? uploadGroupImage(_file, groupId, (p) =>
            progressRef?.current?.setProgress(p),
          )
        : uploadImage(_file, name, siteId, Boolean(isPartnerPortal), (p) =>
            progressRef?.current?.setProgress(p),
          ),
    {
      onError: (e: Error) => {
        toast({
          status: 'error',
          title: e?.message,
          duration: 3000,
          isClosable: true,
        });

        if (siteId) {
          loadingHelpers?.setValue(false);
        }

        helpers.setValue(meta.initialValue || '');

        progressRef?.current?.setProgress(0);
      },
      onSuccess: (fileName) => {
        helpers.setValue(fileName);

        if (siteId) {
          loadingHelpers?.setValue(false);
        }

        progressRef?.current?.setProgress(0);
      },
    },
  );

  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      setSelectedImage(file);
    }
  }, []);

  const onInputClick = useCallback(
    (_isDisabled: boolean) => () => {
      if (_isDisabled) {
        return;
      }

      inputRef?.current?.click();
    },
    [],
  );

  const onCropComplete = useCallback(
    (croppedFile: File) => {
      setSelectedImage(null);

      if (siteId) {
        loadingHelpers?.setValue(true);
      }

      mutate(croppedFile);
    },
    [siteId],
  );

  return (
    <FormControl
      userSelect="none"
      mt={3}
      {...containerProps}
      position={undefined}>
      {label && (
        <FormLabel
          textTransform="capitalize"
          fontSize="12px"
          color="#afafaf"
          {...labelProps}>
          {label}
        </FormLabel>
      )}
      <Box
        px="10px"
        h="40px"
        display={isLoading ? 'none' : 'flex'}
        alignItems="center"
        gap="10px"
        borderRadius="5px"
        color="primary.300"
        bg="primary.900"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        border="2px dashed #2FB64A"
        opacity={isDisabled ? 0.5 : 1}
        onClick={isLoading ? undefined : onInputClick(isDisabled)}>
        <input
          id={name}
          type="file"
          ref={inputRef}
          accept="image/jpg, image/jpeg, image/png"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {Boolean(field?.value) ? (
          <>
            <Box w="100%">
              <Text w="100%" fontSize="14px" noOfLines={1} maxW="180px">
                {field.value}
              </Text>
            </Box>
            <Box
              as={IoMdEye}
              size="20px"
              onClick={(e: any) => {
                e.stopPropagation();

                previewRef?.current?.onOpen?.(field.value);
              }}
            />
            {isSuccess && <MdCheckCircle size="18px" />}
          </>
        ) : (
          <>
            <MdAddAPhoto size="22px" />
            <Text fontSize="14px" mt="2px">
              {groupId ? `Upload group ${name}` : `Upload site ${name}`}
            </Text>
          </>
        )}
      </Box>

      <Box
        px="10px"
        py="8px"
        borderRadius="5px"
        bg="primary.900"
        border="2px dashed #2FB64A"
        display={isLoading ? 'block' : 'none'}>
        <Progress ref={progressRef} />
      </Box>

      {selectedImage && (
        <ImageCropper
          file={selectedImage}
          aspectRatio={aspectRatio}
          onCropComplete={onCropComplete}
          onCancel={() => setSelectedImage(null)}
        />
      )}

      <Preview ref={previewRef} />
    </FormControl>
  );
};

export default FormikImageInputView;
